import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Boton } from "../../components/Boton";
import { ConfirmacionDialog } from "../../components/ConfirmacionDialog";
import { EstadoVacio } from "../../components/EstadoVacio";
import { Insignia } from "../../components/Insignia";
import { Tarjeta } from "../../components/Tarjeta";
import { useToast } from "../../components/Toast";
import { api, ErrorApi } from "../../lib/apiClient";
import {
  accionesVencimientoDisponibles,
  enlaceExpediente,
  type CategoriaCola,
  type EstadoDestinoAgenda,
  type GrupoColaTrabajo,
  type ItemColaTrabajo,
} from "./colaTrabajo";

// Acciones rápidas de vencimientos: mismo copy/patrón de confirmación que ya
// usa AgendaPage.tsx (CONFIRMACION_POR_ACCION) — duplicado acá deliberadamente
// (es texto presentacional, no una regla de negocio) para no tener que
// exportar nada de la página de Agenda, módulo cerrado. La transición real
// (qué acciones ofrecer por estado) vive en accionesVencimientoDisponibles()
// (colaTrabajo.ts), no acá.
const CONFIRMACION_ACCION_VENCIMIENTO: Record<
  EstadoDestinoAgenda,
  { titulo: string; mensaje: (titulo: string) => string; textoConfirmar: string; peligroso?: boolean }
> = {
  Cumplido: {
    titulo: "Cumplir vencimiento",
    mensaje: (t) => `¿Marcar "${t}" como cumplido?`,
    textoConfirmar: "Cumplir",
  },
  Cancelado: {
    titulo: "Cancelar vencimiento",
    mensaje: (t) => `¿Cancelar "${t}"? Vas a poder reabrirlo después si fue un error.`,
    textoConfirmar: "Cancelar vencimiento",
    peligroso: true,
  },
  Pendiente: {
    titulo: "Reabrir vencimiento",
    mensaje: (t) => `¿Volver a marcar "${t}" como pendiente?`,
    textoConfirmar: "Reabrir",
  },
};

const MESES = [
  "ene", "feb", "mar", "abr", "may", "jun",
  "jul", "ago", "sep", "oct", "nov", "dic",
];

function formatearFechaCorta(fecha: string): string {
  const [, mes, dia] = fecha.split("-").map(Number);
  return `${dia} ${MESES[mes - 1]}`;
}

function diasEntre(fechaAnterior: string, fechaPosterior: string): number {
  const [a1, a2, a3] = fechaAnterior.split("-").map(Number);
  const [b1, b2, b3] = fechaPosterior.split("-").map(Number);
  const inicio = Date.UTC(a1, a2 - 1, a3);
  const fin = Date.UTC(b1, b2 - 1, b3);
  return Math.round((fin - inicio) / 86_400_000);
}

// Mismo mapa severidad → tono que ya usa el backend para etapas dentro de
// alertasCriticas (ver agregar('etapa_vencida', ..., 'error') /
// agregar('etapa_sin_responsable'/'etapa_por_vencer', ..., 'advertencia') en
// functions/_app/lib/centro-operaciones.ts) — se porta acá tal cual, nunca
// se reinventa un criterio de severidad nuevo para etapas.
const SEVERIDAD_ETAPA: Partial<Record<CategoriaCola, "error" | "advertencia">> = {
  etapa_vencida: "error",
  etapa_sin_responsable: "advertencia",
  etapa_por_vencer: "advertencia",
};

type Tono = "error" | "advertencia" | "neutro";

// Texto + tono del "estado relevante" de la fila. Dos correcciones sobre la
// versión anterior:
// 1) Las 3 categorías de etapa NO tienen una fecha de vencimiento en
//    `item.fecha` (ahí viaja `etapa_desde`, la fecha en que ENTRÓ a la
//    etapa — casi siempre pasada) — compararla contra hoy como si fuera un
//    vencimiento hacía que CUALQUIER etapa se mostrara "Vencido" en rojo,
//    incluida "sin responsable" o "por vencer" sin ningún problema de plazo
//    real. Ahora la etiqueta/tono de estas 3 categorías sale de
//    SEVERIDAD_ETAPA (idéntico criterio que ya usa el backend), no de una
//    resta de fechas.
// 2) `mostrarAntiguedad` (opt-in): cuando está activo, "Vencido" pasa a
//    "Vencido hace N días" — nunca cambia el tono (rojo sigue siendo rojo),
//    sólo hace visible la magnitud real para no tratar igual algo vencido
//    hace 2 días que hace 687 (spec Centro de Operaciones). Mi Trabajo no
//    pasa este flag y sigue mostrando "Vencido" tal cual mostraba antes.
export function estadoRelevante(item: ItemColaTrabajo, hoyIso: string, mostrarAntiguedad = false): { texto: string; tono: Tono } {
  const severidadEtapa = SEVERIDAD_ETAPA[item.categoria];
  if (item.categoria === "etapa_sin_responsable") return { texto: "Sin responsable", tono: severidadEtapa! };
  if (item.categoria === "etapa_vencida") return { texto: "Vencido", tono: severidadEtapa! };
  if (item.categoria === "etapa_por_vencer") return { texto: "Por vencer", tono: severidadEtapa! };
  if (!item.fecha) return { texto: item.seccion === "urgente" ? "Urgente" : "Sin fecha", tono: item.seccion === "urgente" ? "error" : "neutro" };
  if (item.fecha < hoyIso) {
    if (mostrarAntiguedad) {
      const dias = diasEntre(item.fecha, hoyIso);
      return { texto: `Vencido hace ${dias} ${dias === 1 ? "día" : "días"}`, tono: "error" };
    }
    return { texto: "Vencido", tono: "error" };
  }
  if (item.fecha === hoyIso) return { texto: "Hoy", tono: "error" };
  return { texto: `Vence ${formatearFechaCorta(item.fecha)}`, tono: "neutro" };
}

const CLASE_TONO: Record<Tono, string> = {
  error: "text-error",
  advertencia: "text-advertencia",
  neutro: "text-texto-secundario",
};

function FilaAccion({
  item,
  hoyIso,
  mostrarAntiguedad,
  origen,
  puedeEditarAgenda,
  onSolicitarAccion,
}: {
  item: ItemColaTrabajo;
  hoyIso: string;
  mostrarAntiguedad: boolean;
  origen: string;
  puedeEditarAgenda: boolean;
  onSolicitarAccion: (item: ItemColaTrabajo, accion: EstadoDestinoAgenda) => void;
}) {
  const estado = estadoRelevante(item, hoyIso, mostrarAntiguedad);
  // El backend (PATCH /agenda/:id/estado, requierePermiso("agenda.editar"))
  // sigue siendo la autoridad real — ocultar el botón acá es sólo UX, no
  // seguridad (ver comentario en ColaTrabajoVista más abajo).
  const acciones = puedeEditarAgenda ? accionesVencimientoDisponibles(item) : [];
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-texto">
          <span className="text-secundario">{item.verbo}</span>
          <span className="text-texto-secundario"> · {item.origenEtiqueta}</span>
        </p>
        <p className="mt-0.5 truncate text-sm text-texto-secundario">{item.descripcion}</p>
      </div>
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
        <span className={`text-xs font-medium ${CLASE_TONO[estado.tono]}`}>{estado.texto}</span>
        <Insignia texto={item.prioridad} tipo="prioridad" />
        {acciones.map((accion) => (
          <Boton
            key={accion}
            tamano="sm"
            variante={accion === "Cancelado" ? "fantasma" : "secundario"}
            onClick={() => onSolicitarAccion(item, accion)}
          >
            {CONFIRMACION_ACCION_VENCIMIENTO[accion].textoConfirmar}
          </Boton>
        ))}
        {item.expedienteId && (
          <Link
            to={enlaceExpediente(item.expedienteId, item.destinoPestana)}
            state={{ from: origen }}
            className="rounded-lg border border-borde bg-superficie px-2.5 py-1.5 text-xs font-medium text-texto transition-colors hover:bg-fondo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secundario focus-visible:ring-offset-2"
          >
            Abrir
          </Link>
        )}
      </div>
    </li>
  );
}

// Un expediente = un contenedor (spec §3): siempre visible el encabezado
// (número, cliente, prioridad máxima, cantidad); los ítems marcados
// `siempreVisible` nunca quedan detrás de "Ver más" — sólo el resto
// (prioridad baja/media fuera de las 48hs) puede colapsarse.
//
// mostrarResponsable/mostrarUnidadNegocio/mostrarAntiguedad: opt-in, default
// false — Mi Trabajo no los pasa (el responsable ahí ya es implícito,
// "vos", y "Vencido" a secas ya le alcanza a quien mira su propia cola), así
// que su render queda idéntico a como estaba. Centro de Operaciones sí los
// pasa (spec: "hermano mayor" de Mi Trabajo, mismo componente, coordinador
// ve de quién es cada expediente, a qué Unidad pertenece y hace cuánto está
// vencido cada pendiente sin abrirlo).
//
// `origen` = la ruta+querystring desde la que se entra (para que "Abrir" y
// el link al expediente dejen `location.state.from` y el botón Volver de la
// ficha regrese exactamente ahí — mismo mecanismo de navegación segura ya
// usado por ExpedientesListaPage, ver esRutaInternaSegura()/BotonVolver).
function TarjetaExpediente({
  grupo,
  hoyIso,
  mostrarResponsable = false,
  mostrarUnidadNegocio = false,
  mostrarAntiguedad = false,
  origen,
  puedeEditarAgenda,
  onSolicitarAccion,
}: {
  grupo: GrupoColaTrabajo;
  hoyIso: string;
  mostrarResponsable?: boolean;
  mostrarUnidadNegocio?: boolean;
  mostrarAntiguedad?: boolean;
  origen: string;
  puedeEditarAgenda: boolean;
  onSolicitarAccion: (item: ItemColaTrabajo, accion: EstadoDestinoAgenda) => void;
}) {
  const [expandido, setExpandido] = useState(false);
  const siempreVisibles = grupo.items.filter((i) => i.siempreVisible);
  const colapsables = grupo.items.filter((i) => !i.siempreVisible);

  return (
    <Tarjeta tamano="normal" className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-borde pb-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {grupo.expedienteId ? (
              <Link
                to={`/expedientes/${grupo.expedienteId}?tab=datos`}
                state={{ from: origen }}
                className="font-semibold text-texto hover:text-secundario hover:underline"
              >
                {grupo.numeroExpediente ?? "Expediente"}
              </Link>
            ) : (
              <span className="font-semibold text-texto">Sin expediente asociado</span>
            )}
            <Insignia texto={grupo.prioridadMaxima} tipo="prioridad" />
            {mostrarUnidadNegocio && grupo.areaPracticaNombre && (
              <span className="rounded-full bg-fondo px-2.5 py-1 text-xs font-medium text-texto-secundario">
                {grupo.areaPracticaNombre}
              </span>
            )}
          </div>
          {grupo.clienteNombre && <p className="mt-0.5 text-sm text-texto-secundario">{grupo.clienteNombre}</p>}
          {mostrarResponsable && (
            <p className="mt-0.5 text-sm text-texto-secundario">
              Responsable: {grupo.responsableNombre ?? "Sin asignar"}
            </p>
          )}
        </div>
        <span className="flex-shrink-0 rounded-full bg-fondo px-2.5 py-1 text-xs font-medium text-texto-secundario">
          {grupo.items.length} {grupo.items.length === 1 ? "pendiente" : "pendientes"}
        </span>
      </div>
      <ul className="divide-y divide-borde">
        {siempreVisibles.map((item) => (
          <FilaAccion
            key={item.clave}
            item={item}
            hoyIso={hoyIso}
            mostrarAntiguedad={mostrarAntiguedad}
            origen={origen}
            puedeEditarAgenda={puedeEditarAgenda}
            onSolicitarAccion={onSolicitarAccion}
          />
        ))}
        {expandido &&
          colapsables.map((item) => (
            <FilaAccion
              key={item.clave}
              item={item}
              hoyIso={hoyIso}
              mostrarAntiguedad={mostrarAntiguedad}
              origen={origen}
              puedeEditarAgenda={puedeEditarAgenda}
              onSolicitarAccion={onSolicitarAccion}
            />
          ))}
      </ul>
      {colapsables.length > 0 && (
        <button
          type="button"
          onClick={() => setExpandido((v) => !v)}
          className="mt-1 text-sm font-medium text-secundario hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secundario focus-visible:ring-offset-2"
        >
          {expandido ? "Ver menos" : `Ver ${colapsables.length} más`}
        </button>
      )}
    </Tarjeta>
  );
}

// Cola de trabajo: reemplaza los bloques de KPIs/listas separadas por un
// único árbol expediente → ítems accionables. `hoyIso` se pasa desde el
// componente padre (nunca se recalcula acá) para que toda la pantalla
// comparta la misma noción de "hoy". `origen` (pathname + querystring de
// esta misma pantalla) se calcula una sola vez acá con useLocation() y baja
// a cada Link para que "Volver" desde la ficha regrese exactamente a este
// filtro/período, no a un fallback genérico.
export function ColaTrabajoVista({
  grupos,
  hoyIso,
  mostrarResponsable = false,
  mostrarUnidadNegocio = false,
  mostrarAntiguedad = false,
  puedeEditarAgenda = false,
  onAccionCompletada,
}: {
  grupos: GrupoColaTrabajo[];
  hoyIso: string;
  mostrarResponsable?: boolean;
  mostrarUnidadNegocio?: boolean;
  mostrarAntiguedad?: boolean;
  // Acciones rápidas de vencimientos (Cumplir/Cancelar/Reabrir): opt-in,
  // default false — ningún consumidor existente las pedía, así que sin este
  // prop el componente sigue exactamente igual que antes. Quien lo pasa en
  // true es responsable de calcularlo desde permisos.has("agenda.editar")
  // (mismo permiso real que ya exige PATCH /agenda/:id/estado en el
  // backend — esto es sólo UX, no reemplaza esa verificación).
  puedeEditarAgenda?: boolean;
  // Invocado tras un PATCH exitoso para que el padre vuelva a pedir sus
  // datos al backend (mismo mecanismo de refetch que cada pantalla ya usa,
  // nunca un estado optimista local).
  onAccionCompletada?: () => void;
}) {
  const location = useLocation();
  const origen = location.pathname + location.search;
  const { mostrar } = useToast();
  const [confirmando, setConfirmando] = useState<{ item: ItemColaTrabajo; accion: EstadoDestinoAgenda } | null>(null);
  const [confirmandoCargando, setConfirmandoCargando] = useState(false);

  async function confirmarAccion() {
    if (!confirmando?.item.agendaId) return;
    setConfirmandoCargando(true);
    try {
      await api.patch(`/agenda/${confirmando.item.agendaId}/estado`, { estado: confirmando.accion });
      mostrar(`Evento marcado como ${confirmando.accion.toLowerCase()}.`, "exito");
      setConfirmando(null);
      onAccionCompletada?.();
    } catch (err) {
      // Error HTTP real: el diálogo queda abierto y el ítem no cambia acá
      // (nunca un estado falseado localmente) — recién se refleja algo
      // nuevo si un reintento exitoso llama a onAccionCompletada().
      mostrar(err instanceof ErrorApi ? err.message : "No se pudo actualizar el vencimiento.", "error");
    } finally {
      setConfirmandoCargando(false);
    }
  }

  if (grupos.length === 0) {
    return <EstadoVacio titulo="Sin pendientes" descripcion="No hay ítems urgentes ni del período elegido. Todo al día." />;
  }
  return (
    <div className="space-y-4">
      {grupos.map((grupo) => (
        <TarjetaExpediente
          key={grupo.expedienteId ?? "sin-expediente"}
          grupo={grupo}
          hoyIso={hoyIso}
          mostrarResponsable={mostrarResponsable}
          mostrarUnidadNegocio={mostrarUnidadNegocio}
          mostrarAntiguedad={mostrarAntiguedad}
          origen={origen}
          puedeEditarAgenda={puedeEditarAgenda}
          onSolicitarAccion={(item, accion) => setConfirmando({ item, accion })}
        />
      ))}
      {confirmando && (
        <ConfirmacionDialog
          abierto
          titulo={CONFIRMACION_ACCION_VENCIMIENTO[confirmando.accion].titulo}
          mensaje={CONFIRMACION_ACCION_VENCIMIENTO[confirmando.accion].mensaje(confirmando.item.descripcion)}
          cargando={confirmandoCargando}
          onConfirmar={confirmarAccion}
          onCancelar={() => setConfirmando(null)}
          textoConfirmar={CONFIRMACION_ACCION_VENCIMIENTO[confirmando.accion].textoConfirmar}
          peligroso={CONFIRMACION_ACCION_VENCIMIENTO[confirmando.accion].peligroso}
        />
      )}
    </div>
  );
}
